import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, ChevronDown, ChevronUp, BookOpen, FileText, Download, AlertTriangle, Video, Film, Calendar, Clock, ExternalLink, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { useAntiPiracy } from "@/hooks/useAntiPiracy";
import ProtectedVideoPlayer from "@/components/ProtectedVideoPlayer";
import QuizPlayer from "@/components/QuizPlayer";

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const match = url.match(p);
    if (match) return match[1];
  }
  return null;
}

function isYouTubeUrl(url) {
  if (!url) return false;
  return url.includes("youtube.com") || url.includes("youtu.be");
}

function getGDriveEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match) return `https://drive.google.com/file/d/${match[1]}/preview`;
  return null;
}

function getSessionStatus(s) {
  const now = new Date();
  const sessionTime = new Date(`${s.scheduled_date}T${s.scheduled_time}`);
  const endTime = new Date(sessionTime.getTime() + (s.duration_minutes || 60) * 60000);
  if (now >= sessionTime && now <= endTime) return "live";
  if (now > endTime) return "completed";
  return "upcoming";
}

function formatSessionDate(dateStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  } catch { return dateStr; }
}

function formatSessionTime(timeStr) {
  try {
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(parseInt(h), parseInt(m));
    return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return timeStr; }
}

export default function WatchPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { shouldProtect, user } = useAntiPiracy();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [notes, setNotes] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeTab, setActiveTab] = useState("content");
  const [videoError, setVideoError] = useState("");
  const [videoKey, setVideoKey] = useState(0);
  const [playingRecording, setPlayingRecording] = useState(null);
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [myAttempts, setMyAttempts] = useState({});

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchData = async () => {
    try {
      const [courseRes, chaptersRes, notesRes, liveRes, recRes, quizRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/chapters`),
        api.get(`/courses/${courseId}/notes`).catch(() => ({ data: [] })),
        api.get(`/courses/${courseId}/live-sessions`).catch(() => ({ data: [] })),
        api.get(`/courses/${courseId}/recorded-sessions`).catch(() => ({ data: [] })),
        api.get(`/quizzes/course/${courseId}`).catch(() => ({ data: [] })),
      ]);
      setCourse(courseRes.data);
      setChapters(chaptersRes.data);
      setNotes(notesRes.data);
      setLiveSessions(liveRes.data);
      setRecordings(recRes.data);
      setQuizzes(quizRes.data.filter(q => q.is_active));
      // Fetch attempts for each quiz
      const attemptsMap = {};
      for (const q of quizRes.data) {
        try {
          const { data } = await api.get(`/quizzes/${q.id}/my-attempts`);
          attemptsMap[q.id] = data;
        } catch { attemptsMap[q.id] = []; }
      }
      setMyAttempts(attemptsMap);
      if (chaptersRes.data.length > 0) {
        setExpandedChapter(chaptersRes.data[0].id);
        if (chaptersRes.data[0].videos?.length > 0) {
          setCurrentVideo(chaptersRes.data[0].videos[0]);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const selectVideo = (video) => {
    setPlaying(false);
    setProgress(0);
    setVideoError("");
    setPlayingRecording(null);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
    setCurrentVideo(video);
    setVideoKey(prev => prev + 1);
  };

  const selectRecording = (rec) => {
    setPlaying(false);
    setVideoError("");
    setCurrentVideo(null);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
    setPlayingRecording(rec);
    setVideoKey(prev => prev + 1);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setProgress(videoRef.current.currentTime);
      setDuration(videoRef.current.duration || 0);
    }
  };

  const preventContextMenu = (e) => e.preventDefault();

  const upcomingSessions = useMemo(() => liveSessions.filter(s => getSessionStatus(s) !== "completed" && s.status !== "cancelled"), [liveSessions]);
  const liveSess = useMemo(() => liveSessions.filter(s => getSessionStatus(s) === "live"), [liveSessions]);

  const startQuiz = async (quiz) => {
    try {
      const { data } = await api.get(`/quizzes/${quiz.id}`);
      setActiveQuiz(data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to load quiz");
    }
  };

  // If quiz is active, show full-screen QuizPlayer
  if (activeQuiz) {
    return (
      <QuizPlayer
        quiz={activeQuiz}
        onClose={() => { setActiveQuiz(null); fetchData(); }}
      />
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  const activeVideoUrl = playingRecording?.video_url || currentVideo?.url;
  const activeVideoTitle = playingRecording?.title || currentVideo?.title;
  const isActiveYouTube = isYouTubeUrl(activeVideoUrl);
  const activeYouTubeId = extractYouTubeId(activeVideoUrl);
  const activeGDriveEmbed = getGDriveEmbedUrl(activeVideoUrl);

  return (
    <div className="bg-slate-900 min-h-screen" data-testid="watch-page" onContextMenu={preventContextMenu}>
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-3 bg-black/50 text-white">
        <button data-testid="watch-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-sm font-bold truncate flex-1">{course?.title}</h1>
      </div>

      {/* Video Player Area */}
      <ProtectedVideoPlayer
        videoUrl={activeVideoUrl}
        videoTitle={activeVideoTitle}
        videoKey={videoKey}
        isYouTube={isActiveYouTube}
        youTubeId={activeYouTubeId}
        gDriveEmbed={activeGDriveEmbed}
        shouldProtect={shouldProtect}
        user={user}
        courseId={courseId}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onError={(e) => {
          const code = e.target?.error?.code;
          if (code === 4) setVideoError("This video format is not supported.");
          else if (code === 2) setVideoError("Network error. Check your connection.");
          else setVideoError("Video failed to load.");
        }}
        videoError={videoError}
        setVideoError={setVideoError}
        setVideoKey={setVideoKey}
      />

      {/* Current Video Info */}
      {activeVideoTitle && (
        <div className="px-4 py-3 bg-slate-800 text-white flex items-center gap-2">
          {playingRecording && <Film size={14} className="text-amber-400" />}
          <p className="text-sm font-bold">{activeVideoTitle}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex bg-slate-800 border-b border-slate-700 overflow-x-auto" data-testid="watch-tabs">
        <button
          data-testid="tab-content"
          onClick={() => setActiveTab("content")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold transition-none whitespace-nowrap ${
            activeTab === "content" ? "text-[#0284C7] border-b-2 border-[#0284C7] bg-slate-900" : "text-slate-400"
          }`}
        >
          <BookOpen size={13} /> Content
        </button>
        <button
          data-testid="tab-live"
          onClick={() => setActiveTab("live")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold transition-none whitespace-nowrap relative ${
            activeTab === "live" ? "text-red-400 border-b-2 border-red-400 bg-slate-900" : "text-slate-400"
          }`}
        >
          <Video size={13} /> Live
          {liveSess.length > 0 && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
          {upcomingSessions.length > 0 && <span className="bg-slate-600 text-slate-300 text-[9px] px-1 py-0.5 rounded">{upcomingSessions.length}</span>}
        </button>
        <button
          data-testid="tab-recordings"
          onClick={() => setActiveTab("recordings")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold transition-none whitespace-nowrap ${
            activeTab === "recordings" ? "text-amber-400 border-b-2 border-amber-400 bg-slate-900" : "text-slate-400"
          }`}
        >
          <Film size={13} /> Recordings
          {recordings.length > 0 && <span className="bg-slate-600 text-slate-300 text-[9px] px-1 py-0.5 rounded">{recordings.length}</span>}
        </button>
        <button
          data-testid="tab-quiz"
          onClick={() => setActiveTab("quiz")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold transition-none whitespace-nowrap ${
            activeTab === "quiz" ? "text-purple-400 border-b-2 border-purple-400 bg-slate-900" : "text-slate-400"
          }`}
        >
          <ClipboardList size={13} /> Quiz
          {quizzes.length > 0 && <span className="bg-slate-600 text-slate-300 text-[9px] px-1 py-0.5 rounded">{quizzes.length}</span>}
        </button>
        <button
          data-testid="tab-notes"
          onClick={() => setActiveTab("notes")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold transition-none whitespace-nowrap ${
            activeTab === "notes" ? "text-green-400 border-b-2 border-green-400 bg-slate-900" : "text-slate-400"
          }`}
        >
          <FileText size={13} /> Notes
          {notes.length > 0 && <span className="bg-slate-600 text-slate-300 text-[9px] px-1 py-0.5 rounded">{notes.length}</span>}
        </button>
      </div>

      {/* TAB: Course Content */}
      {activeTab === "content" && (
        <div className="bg-slate-900" data-testid="chapter-list">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="text-sm font-bold text-white">Course Content</h2>
          </div>
          {chapters.map((ch) => (
            <div key={ch.id}>
              <button
                data-testid={`chapter-${ch.id}`}
                onClick={() => setExpandedChapter(expandedChapter === ch.id ? null : ch.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left border-b border-slate-700/50"
              >
                <span className="text-sm font-medium text-slate-200 flex-1 truncate pr-2">{ch.title}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-slate-500">{ch.videos?.length || 0} videos</span>
                  {expandedChapter === ch.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </div>
              </button>
              {expandedChapter === ch.id && ch.videos?.map((video) => (
                <button
                  key={video.id}
                  data-testid={`video-${video.id}`}
                  onClick={() => { selectVideo(video); setActiveTab("content"); }}
                  className={`w-full flex items-center gap-3 px-6 py-2.5 text-left ${
                    currentVideo?.id === video.id && !playingRecording ? "bg-[#0284C7]/20" : "bg-slate-800/50"
                  }`}
                >
                  <Play size={14} className={currentVideo?.id === video.id && !playingRecording ? "text-[#0284C7]" : "text-slate-500"} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs truncate ${currentVideo?.id === video.id && !playingRecording ? "text-[#0284C7] font-bold" : "text-slate-300"}`}>
                      {video.title}
                    </p>
                    {video.duration && <p className="text-[10px] text-slate-500">{video.duration}</p>}
                  </div>
                </button>
              ))}
            </div>
          ))}
          {chapters.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">No content available yet.</div>
          )}
        </div>
      )}

      {/* TAB: Live Classes */}
      {activeTab === "live" && (
        <div className="bg-slate-900">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Video size={14} className="text-red-400" /> Live Classes
            </h2>
          </div>

          {liveSessions.length > 0 ? (
            <div className="divide-y divide-slate-700/50">
              {liveSessions.filter(s => s.status !== "cancelled").map((s) => {
                const status = getSessionStatus(s);
                const isLive = status === "live";
                const isCompleted = status === "completed";
                return (
                  <div key={s.id} data-testid={`live-session-${s.id}`} className={`px-4 py-4 ${isLive ? "bg-red-500/5" : ""}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isLive ? "bg-red-500 animate-pulse" : isCompleted ? "bg-slate-700" : "bg-[#0284C7]"
                      }`}>
                        <Video size={18} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            isLive ? "bg-red-500 text-white" : isCompleted ? "bg-slate-600 text-slate-300" : "bg-green-500/20 text-green-400"
                          }`}>
                            {isLive ? "LIVE NOW" : isCompleted ? "ENDED" : "UPCOMING"}
                          </span>
                        </div>
                        <h3 className="text-sm font-bold text-white">{s.title}</h3>
                        {s.description && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{s.description}</p>}
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                          <span className="flex items-center gap-1"><Calendar size={11} /> {formatSessionDate(s.scheduled_date)}</span>
                          <span className="flex items-center gap-1"><Clock size={11} /> {formatSessionTime(s.scheduled_time)}</span>
                          <span>{s.duration_minutes} min</span>
                        </div>

                        {/* Join button */}
                        {(isLive || !isCompleted) && s.meet_link && (
                          <a
                            href={s.meet_link.startsWith("http") ? s.meet_link : "https://" + s.meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`join-live-${s.id}`}
                            className={`inline-flex items-center gap-2 mt-3 text-sm font-bold px-5 py-2.5 rounded-lg ${
                              isLive
                                ? "bg-red-500 text-white shadow-lg shadow-red-500/30"
                                : "bg-[#0284C7] text-white"
                            }`}
                          >
                            <ExternalLink size={14} />
                            {isLive ? "Join Live Class" : "Open Meet Link"}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <Video size={40} className="mx-auto text-slate-600 mb-3" />
              <h3 className="text-sm font-bold text-slate-400 mb-1">No Live Classes</h3>
              <p className="text-xs text-slate-500">Live classes will appear here when scheduled.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Recorded Sessions */}
      {activeTab === "recordings" && (
        <div className="bg-slate-900">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Film size={14} className="text-amber-400" /> Recorded Sessions
            </h2>
            <p className="text-[10px] text-slate-500 mt-0.5">{recordings.length} recordings available</p>
          </div>

          {recordings.length > 0 ? (
            <div className="divide-y divide-slate-700/50">
              {recordings.map((rec) => {
                const recYtId = extractYouTubeId(rec.video_url);
                const isActive = playingRecording?.id === rec.id;
                return (
                  <button
                    key={rec.id}
                    data-testid={`recording-${rec.id}`}
                    onClick={() => selectRecording(rec)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left ${isActive ? "bg-amber-500/10" : ""}`}
                  >
                    <div className={`w-12 aspect-video flex-shrink-0 overflow-hidden rounded ${isActive ? "ring-2 ring-amber-400" : "bg-slate-800"}`}>
                      {recYtId ? (
                        <img src={`https://img.youtube.com/vi/${recYtId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                          <Film size={14} className="text-slate-500" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${isActive ? "text-amber-400" : "text-slate-200"}`}>{rec.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {rec.duration && <span className="text-[10px] text-slate-500">{rec.duration}</span>}
                        {rec.recorded_date && <span className="text-[10px] text-slate-500">{rec.recorded_date}</span>}
                      </div>
                      {rec.description && <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{rec.description}</p>}
                    </div>
                    <Play size={16} className={isActive ? "text-amber-400" : "text-slate-600"} />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <Film size={40} className="mx-auto text-slate-600 mb-3" />
              <h3 className="text-sm font-bold text-slate-400 mb-1">No Recordings Yet</h3>
              <p className="text-xs text-slate-500">Past session recordings will appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Notes */}
      {activeTab === "notes" && (
        <div className="bg-slate-900">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <FileText size={14} className="text-green-400" /> Free PDF Notes
            </h2>
            <p className="text-[10px] text-slate-500 mt-0.5">{notes.length} notes available</p>
          </div>
          {notes.length > 0 ? (
            <div className="divide-y divide-slate-700/50">
              {notes.map((note) => {
                const chapterName = chapters.find(c => c.id === note.chapter_id)?.title;
                return (
                  <div key={note.id} data-testid={`note-${note.id}`} className="px-4 py-3 flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <FileText size={18} className="text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200">{note.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {chapterName && <span className="text-[10px] text-[#0284C7]">{chapterName}</span>}
                        {note.pages && <span className="text-[10px] text-slate-500">{note.pages}</span>}
                      </div>
                    </div>
                    <a href={note.file_url} target="_blank" rel="noreferrer" data-testid={`download-note-${note.id}`} className="flex items-center gap-1.5 bg-green-600 text-white text-xs font-bold px-3 py-2 transition-none flex-shrink-0">
                      <Download size={14} /> PDF
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <FileText size={40} className="mx-auto text-slate-600 mb-3" />
              <h3 className="text-sm font-bold text-slate-400 mb-1">No Notes Available</h3>
              <p className="text-xs text-slate-500">PDF notes will appear here when uploaded.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Quiz */}
      {activeTab === "quiz" && (
        <div className="bg-slate-900">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <ClipboardList size={14} className="text-purple-400" /> Chapter Quizzes
            </h2>
            <p className="text-[10px] text-slate-500 mt-0.5">{quizzes.length} quizzes available</p>
          </div>

          {quizzes.length > 0 ? (
            <div className="divide-y divide-slate-700/50">
              {quizzes.map(quiz => {
                const ch = chapters.find(c => c.id === quiz.chapter_id);
                const attempts = myAttempts[quiz.id] || [];
                const bestAttempt = attempts.length > 0 ? attempts.reduce((best, a) => a.percentage > best.percentage ? a : best, attempts[0]) : null;
                const attemptsLeft = quiz.max_attempts - attempts.length;
                return (
                  <div key={quiz.id}>
                    {ch && (
                      <div className="px-4 py-2 bg-slate-800/50">
                        <p className="text-[10px] text-[#0284C7] font-bold uppercase">{ch.title}</p>
                      </div>
                    )}
                    <div data-testid={`quiz-card-${quiz.id}`} className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <ClipboardList size={18} className="text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-white">{quiz.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                            <span>{quiz.question_count} questions</span>
                            <span><Clock size={10} className="inline mr-0.5" />{quiz.time_limit_minutes} min</span>
                            <span>Pass: {quiz.pass_percentage}%</span>
                          </div>
                          {bestAttempt && (
                            <div className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold ${bestAttempt.passed ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                              Best: {bestAttempt.percentage}% ({bestAttempt.score}/{bestAttempt.total})
                              {bestAttempt.passed ? " - PASSED" : " - FAILED"}
                            </div>
                          )}
                          <p className="text-[10px] text-slate-500 mt-1">
                            {attempts.length > 0 ? `${attempts.length} attempt(s) used` : "Not attempted"} | {attemptsLeft > 0 ? `${attemptsLeft} left` : "No attempts left"}
                          </p>
                          <button
                            data-testid={`start-quiz-${quiz.id}`}
                            onClick={() => startQuiz(quiz)}
                            disabled={attemptsLeft <= 0}
                            className={`mt-3 w-full text-sm font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 ${
                              attemptsLeft <= 0 ? "bg-slate-700 text-slate-500 cursor-not-allowed" : "bg-purple-600 text-white"
                            }`}
                          >
                            <ClipboardList size={14} />
                            {attemptsLeft <= 0 ? "No Attempts Left" : attempts.length > 0 ? "Retake Quiz" : "Start Quiz"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <ClipboardList size={40} className="mx-auto text-slate-600 mb-3" />
              <h3 className="text-sm font-bold text-slate-400 mb-1">No Quizzes Available</h3>
              <p className="text-xs text-slate-500">Chapter quizzes will appear here when created.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
